"use client";

import { loadStripe } from '@stripe/stripe-js';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { showSuccess, showError } from '@/utils/toast';
import { supabase } from '@/utils/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

interface StripeCheckoutProps {
  onSuccess?: () => void;
}

export const StripeCheckout = ({ onSuccess }: StripeCheckoutProps) => {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<'pro' | 'enterprise'>('pro');
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const { profile } = useAuth();

  const handleCheckout = async () => {
    if (!stripePromise) {
      showError('Stripe not loaded');
      return;
    }

    if (!profile?.company_id) {
      showError('Missing company. Please select or create a company first.');
      return;
    }

    setLoading(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          plan,
          company_id: profile.company_id,
          amount: plan === 'pro' ? 7900 : 39900,
          currency: 'gbp',
          billing_interval: billingInterval,
        },
      });

      if (error) throw new Error(error.message || 'Failed to create checkout session');

      const url = data?.url;
      if (url) {
        window.location.href = url;
        onSuccess?.();
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      showError(error.message || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upgrade Plan</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="plan">Select Plan</Label>
          <select id="plan" value={plan} onChange={(e) => setPlan(e.target.value as 'pro' | 'enterprise')}>
            <option value="pro">Pro (£79/mo)</option>
            <option value="enterprise">Enterprise (£399/mo)</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="billing">Billing Cycle</Label>
          <select id="billing" value={billingInterval} onChange={(e) => setBillingInterval(e.target.value as 'month' | 'year')}>
            <option value="month">Monthly</option>
            <option value="year">Yearly (Save 10%)</option>
          </select>
        </div>
        <Button onClick={handleCheckout} disabled={loading} className="w-full">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {loading ? 'Creating Checkout...' : 'Upgrade Now'}
        </Button>
      </CardContent>
    </Card>
  );
};